import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import QuoteBuilder from "../components/admin/QuoteBuilder";
import { getCustomerTimeline } from "../api/customers";
import { formatMoney } from "../utils/money";
import { formatEasternDate } from "../utils/dates";
import { telHref, smsHref, mailtoHref, timeAgo, firstName, senderName } from "../utils/contact";
import { stageOf, STAGE_META } from "../utils/jobs";
import { useLiveRefresh } from "../utils/live";
import { notifyCountsChanged } from "../utils/navCounts";

// One customer, everything in one place: how to reach them, what they've
// paid and owe, their jobs, and a dated history of every touchpoint.

const ICON = {
  lead: "📥",
  quote: "📝",
  sent: "📤",
  view: "👀",
  request: "🛎️",
  payment: "💰",
  event: "⛳",
  rain: "🌧️",
  credit: "🎟️",
  credit_used: "🎟️",
  lost: "✖️",
  message: "💬",
  email: "✉️",
  touch: "📞",
};

const usd0 = (cents) => `$${Math.round(Number(cents || 0) / 100).toLocaleString()}`;

export default function AdminCustomer() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [builder, setBuilder] = useState(null); // { quoteId } | { new: true }

  const load = useCallback(async () => {
    try {
      setData(await getCustomerTimeline(id));
      setError("");
    } catch (e) {
      setError(e.status === 404 ? "That customer doesn't exist anymore." : e.message || "Could not load the customer.");
    }
  }, [id]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);
  useLiveRefresh(load);

  // ?job=<id> (from search) opens that job straight away.
  const jobParam = params.get("job");
  useEffect(() => {
    if (!jobParam) return;
    setBuilder({ quoteId: Number(jobParam) });
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("job");
        return next;
      },
      { replace: true }
    );
  }, [jobParam, setParams]);

  const c = data?.customer;
  const upcoming = data?.timeline.filter((e) => e.future).reverse() || []; // soonest first
  const history = data?.timeline.filter((e) => !e.future) || [];

  return (
    <AppShell title={c?.full_name || "Customer"}>
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!data ? (
        !error && (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : (
        <div className="space-y-4">
          <section className="bg-white border border-birdie-green/12 rounded-2xl p-4">
            <div className="text-sm text-birdie-charcoal/70 space-y-0.5">
              {c.company_name && <div className="font-semibold text-birdie-charcoal">{c.company_name}</div>}
              {c.phone && <div>{c.phone}</div>}
              {c.email && <div className="break-all">{c.email}</div>}
              <div className="text-xs text-birdie-charcoal/45 pt-1">
                {data.stats.first_seen &&
                  `Customer since ${new Date(data.stats.first_seen).toLocaleDateString([], {
                    month: "short",
                    year: "numeric",
                    timeZone: "America/New_York",
                  })}`}
                {c.heard_from && ` · heard about you via ${c.heard_from}`}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {telHref(c.phone) && <LinkBtn href={telHref(c.phone)}>Call</LinkBtn>}
              {c.phone && (
                <LinkBtn href={smsHref(c.phone, `Hi ${firstName(c.full_name)}, it's ${senderName() || "the team"} from Birdie Bus — `)}>
                  Text
                </LinkBtn>
              )}
              {c.email && <LinkBtn href={mailtoHref(c.email, "Birdie Bus")}>Email</LinkBtn>}
              <Button size="sm" onClick={() => setBuilder({ new: true })} className="ml-auto">
                + New quote
              </Button>
            </div>
          </section>

          <div className="grid grid-cols-2 gap-2.5">
            <Stat label="Paid to date" value={usd0(data.stats.paid_cents)} />
            <Stat label="Events" value={String(data.stats.events)} hint={`${data.stats.quotes} quote${data.stats.quotes === 1 ? "" : "s"} in all`} />
            <Stat label="Still owed" value={usd0(data.stats.owed_cents)} tone={data.stats.owed_cents > 0 ? "warn" : null} />
            <Stat label="Rain credit" value={usd0(data.stats.credit_cents)} hint={data.stats.credit_cents > 0 ? "applies on their next quote" : null} />
          </div>

          {data.jobs.length > 0 && (
            <Section title="Jobs">
              <ul className="divide-y divide-birdie-green/10">
                {data.jobs.map((j) => {
                  const meta = j.rained_out_at ? { label: "Rained out", cls: "bg-sky-100 text-sky-800" } : STAGE_META[stageOf(j)];
                  return (
                    <li key={j.id}>
                      <button
                        type="button"
                        onClick={() => setBuilder({ quoteId: j.id })}
                        className="w-full flex items-center justify-between gap-3 py-2.5 text-left -mx-1 px-1 rounded-lg hover:bg-birdie-cream/60"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-birdie-charcoal truncate">
                            {j.title || "Untitled job"}
                          </span>
                          <span className="block text-[11px] text-birdie-charcoal/50">
                            {j.quote_number}
                            {j.event_date ? ` · ${formatEasternDate(j.event_date)}` : ""}
                          </span>
                        </span>
                        <span className="shrink-0 text-right">
                          <span className="block text-sm font-semibold tabular-nums text-birdie-charcoal">
                            {formatMoney(j.total_cents)}
                          </span>
                          {meta && (
                            <span className={`inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${meta.cls}`}>
                              {meta.label}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {upcoming.length > 0 && (
            <Section title="Coming up">
              <Timeline items={upcoming} onOpen={(qid) => setBuilder({ quoteId: qid })} />
            </Section>
          )}

          <Section title="History">
            {history.length ? (
              <Timeline items={history} onOpen={(qid) => setBuilder({ quoteId: qid })} />
            ) : (
              <p className="text-sm text-birdie-charcoal/55">Nothing yet.</p>
            )}
          </Section>
        </div>
      )}

      {builder && c && (
        <QuoteBuilder
          quoteId={builder.quoteId || null}
          prefill={builder.new ? { customer: c } : null}
          onClose={(changed) => {
            setBuilder(null);
            if (changed) load().then(notifyCountsChanged);
          }}
        />
      )}
    </AppShell>
  );
}

// "12d ago" for recent things; older ones already show their date.
function relative(at) {
  const r = timeAgo(at);
  return /ago|just now/.test(r) ? r : "";
}

function Timeline({ items, onOpen }) {
  return (
    <ol className="relative">
      {items.map((e, i) => {
        const clickable = Boolean(e.quote_id);
        const Tag = clickable ? "button" : "div";
        return (
          <li key={i} className="relative pl-9 pb-3 last:pb-0">
            {i < items.length - 1 && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-birdie-green/15" aria-hidden />}
            <span className="absolute left-0 top-0.5 w-7 h-7 rounded-full bg-birdie-cream flex items-center justify-center text-sm" aria-hidden>
              {ICON[e.kind] || "•"}
            </span>
            <Tag
              {...(clickable ? { type: "button", onClick: () => onOpen(e.quote_id) } : {})}
              className={`w-full text-left ${clickable ? "rounded-lg hover:bg-birdie-cream/60 -mx-1 px-1" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <span className={`text-sm font-medium ${e.kind === "lost" ? "text-red-700" : "text-birdie-charcoal"}`}>{e.title}</span>
                <span className="shrink-0 text-[11px] text-birdie-charcoal/45 text-right">
                  {formatEasternDate(e.at)}
                  <span className="block">{e.future ? "upcoming" : relative(e.at)}</span>
                </span>
              </div>
              {e.detail && <div className="text-xs text-birdie-charcoal/60 break-words">{e.detail}</div>}
            </Tag>
          </li>
        );
      })}
    </ol>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div className="bg-white border border-birdie-green/12 rounded-xl px-3 py-2.5">
      <div className="text-[11px] text-birdie-charcoal/55">{label}</div>
      <div className={`text-xl font-semibold mt-0.5 ${tone === "warn" ? "text-amber-700" : "text-birdie-charcoal"}`}>{value}</div>
      {hint && <div className="text-[10px] text-birdie-charcoal/40 mt-0.5">{hint}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-birdie-green/12 rounded-2xl p-4">
      <h2 className="font-accent text-base text-birdie-charcoal mb-2">{title}</h2>
      {children}
    </section>
  );
}

function LinkBtn({ href, children }) {
  return (
    <a
      href={href}
      className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-birdie-green/20 text-birdie-green hover:bg-birdie-cream"
    >
      {children}
    </a>
  );
}
